import { FC, ReactNode } from "react";
import { createPortal } from "react-dom";

/** ContentPortal component properties. */
interface ContentPortalProps {
  /** The container element to portal content into. */
  readonly container: Element | null;

  /** The component children to render in the portal. */
  readonly children?: ReactNode;
}

/** ContentPortalContainer component properties. */
interface ContentPortalContainerProps {
  /** CSS class name for the container. */
  readonly className?: string;

  /** Ref handler for the container element. */
  readonly refHandler: (ref: HTMLDivElement | null) => void;
}

/**
 * Core primitive for rendering content into a target container via React portals.
 * Handles mount-to-container logic without any framework-specific semantics.
 */
export const ContentPortal: FC<ContentPortalProps> = ({ container, children }) => {
  if (!container) return null;

  return createPortal(children, container);
};

/**
 * Container element for portal content.
 * Provides a ref-enabled div that can be used as a portal target.
 */
export const ContentPortalContainer: FC<ContentPortalContainerProps> = ({ className, refHandler }) => {
  return <div className={className} ref={refHandler} />;
};
